import {
  LearningFragment,
  LearningSummaryType,
  OperatorType,
  SearchFilterDynamicArgType,
  TypeEnumType,
} from "@devforce/trailhead-client-gql";
import {
  CollectionItem,
  CollectionQuery,
  getCollectionItem,
} from "common/contentCollection";
import { QueryAdapterValue, gql } from "common/graphqlAdapter";
import type { Preferences, Product, Solution } from "common/learnerPreferences";
import {
  productGroups,
  solutionToProducts,
} from "../productMapping/productMapping";
import { dedupe } from "common/helpers";

export type QueryResult = QueryAdapterValue<{
  onboardingTrailSearch: LearningSummaryType[];
}>;

export type QueryOptions = {
  limit: number;
  preferences: Preferences;
  transformer?: (
    value: CollectionItem,
    index: number,
    array: CollectionItem[]
  ) => CollectionItem;
};

// GQL query for trail recommendations.
const query = gql`
  query OnboardingTrailSearch($filter: SearchFilterInput) {
    onboardingTrailSearch(filter: $filter) {
      __typename
      ...Learning
    }
  }
  ${LearningFragment}
`;

/**
 * Get options for QueryAdapter.
 * @param preferences - preferences object
 */
export function getQueryOptions(preferences: Preferences) {
  return {
    query,
    variables: {
      filter: {
        dynamic: getDynamicFilters(preferences),
        operator: OperatorType.OrType,
        type: { eq: [TypeEnumType.TrailType] },
      },
    },
  };
}

// Preference keys used to build the query directly or mapped.
type FilterablePreference = keyof Omit<Preferences, "activeTrail">;

// Filters used in the query. Preferences keys get mapped to these.
type QueryFilter = "intent_ids" | "level_ids" | "product_ids" | "role_ids";

// Map FilterablePreference's to QueryFilter's to transform overloaded prefs.
const preferenceKeyMap: Record<FilterablePreference, QueryFilter> = {
  intent: "intent_ids",
  level: "level_ids",
  product: "product_ids",
  role: "role_ids",
  solution: "product_ids",
};

/**
 * Transform preference value to array for query filter.
 * @param prefValue - preference value
 */
function preferenceValueToArray(prefValue: Preferences[FilterablePreference]) {
  let value: string[] = [];
  if (!prefValue) {
    value = [];
  } else if (!Array.isArray(prefValue)) {
    value = [prefValue];
  } else {
    value = prefValue;
  }
  return value;
}

/**
 * Maps solution to array of products.
 * @param solution - solution
 */
function mapSolutionToProducts(solution: string) {
  return solutionToProducts[solution as Solution];
}

/**
 * Maps umbrella product group to a list of products if mapping exists.
 * @param product - product
 */
function mapProductGroups(product: string) {
  return productGroups[product as Product] ?? [product];
}

/**
 * Maps filter value for given preference key if applicable.
 */
function mapFilterValue(prefKey: FilterablePreference, filterValue: string[]) {
  switch (prefKey) {
    case "product":
      // Expand product groups if needed.
      return dedupe(filterValue.map<string[]>(mapProductGroups).flat());
    case "solution":
      // Map solution to array of unique products.
      return dedupe(filterValue.map<string[]>(mapSolutionToProducts).flat());
    default:
      return filterValue;
  }
}

/**
 * Get `filter.dynamic` value for query given preferences.
 * @param preferences - preferences object
 */
function getDynamicFilters(
  preferences: Preferences
): SearchFilterDynamicArgType[] {
  const filters: SearchFilterDynamicArgType[] = [];

  for (const [prefKey, filterName] of Object.entries(preferenceKeyMap)) {
    const key = prefKey as FilterablePreference;
    const prefValue = (preferences ?? {})[key];
    let filterValue = preferenceValueToArray(prefValue);

    if (!filterValue.length) continue;

    filterValue = mapFilterValue(key, filterValue);

    // If filter name already exists, update it; otherwise push it.
    const index = filters.findIndex((f) => f.name === filterName);
    if (index >= 0) {
      filterValue = dedupe(filters[index].eq?.concat(filterValue)) || [];
      filters[index].eq = filterValue;
    } else {
      filters.push({ name: filterName, eq: filterValue });
    }
  }

  // Remove empty eq[] arrays.
  return filters.filter((f) => !!f.eq?.length);
}

export class Query implements CollectionQuery<QueryResult> {
  private options!: QueryOptions;

  constructor(options: QueryOptions) {
    this.options = options;
  }

  getCollectionItems(result: QueryResult) {
    let results = (result.data.onboardingTrailSearch || [])
      .slice(0, this.options.limit)
      .map((learning) => getCollectionItem(learning));
    if (this.options.transformer) {
      results = results.map(this.options.transformer);
    }
    return results;
  }

  getQueryOptions() {
    return getQueryOptions(this.options.preferences);
  }

  hasMore(result: QueryResult) {
    return result.data.onboardingTrailSearch.length > this.options.limit;
  }
}
